import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, Plus, Trash2, GraduationCap, Wand2, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { appConfirm } from "@/components/AppDialogHost";

const SECTION_META = [
  { id: "getting_started",  label: "Getting Started" },
  { id: "content_creation", label: "Content Creation" },
  { id: "voice_audio",      label: "Voice & Audio Studio" },
  { id: "live_onair",       label: "Live & On-Air" },
];

const fmt = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, "0")}`;

const parseDur = (t) => {
  const m = String(t || "").trim().match(/^(\d+):(\d{1,2})$/);
  if (m) return parseInt(m[1]) * 60 + parseInt(m[2]);
  const n = parseInt(t);
  return Number.isFinite(n) ? n : 0;
};

const embedFor = (url) => {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  return { kind: "video", src: url };
};

export default function AcademyVideos() {
  const t = useT();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [playing, setPlaying] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => api.get("/academy/videos").then((r) => setData(r.data)).catch(() => toast.error("Could not load tutorials"));
  useEffect(() => { load(); }, []);

  const videos = useMemo(() => {
    const list = data?.videos || [];
    const needle = q.trim().toLowerCase();
    return needle ? list.filter((v) => v.title.toLowerCase().includes(needle)) : list;
  }, [data, q]);

  const sections = tab === "all" ? SECTION_META : SECTION_META.filter((s) => s.id === tab);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="academy-videos-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-brand-amber" /> Academy
          </h1>
          <p className="text-sm text-ink-secondary mt-1">{t("Short tutorials, one topic at a time")}</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.is_admin && (
            <button onClick={() => setShowAdd(true)} data-testid="academy-add-video-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-xs font-bold text-black hover:opacity-90 transition">
              <Plus className="h-3.5 w-3.5" /> Add video
            </button>
          )}
          <Link to="/dashboard/academy/creator" data-testid="academy-creator-link"
            className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-2 text-xs text-ink-secondary hover:text-ink-primary hover:border-brand-amber/40 transition">
            <Wand2 className="h-3.5 w-3.5" /> Tutorial Creator
          </Link>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tutorials..."
          data-testid="academy-search-input"
          className="w-full rounded-lg border border-line bg-bg-surface pl-9 pr-3 py-2.5 text-sm outline-none focus:border-sky-500/50" />
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {[{ id: "all", label: "All" }, ...SECTION_META].map((s) => (
          <button key={s.id} onClick={() => setTab(s.id)} data-testid={`academy-tab-${s.id}`}
            className={`rounded-lg border px-3.5 py-2 text-xs transition ${
              tab === s.id ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
                           : "border-line bg-bg-surface text-ink-secondary hover:text-ink-primary"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="py-16 text-center text-ink-muted text-sm">Loading…</div>
      ) : sections.map((sec) => {
        const vids = videos.filter((v) => v.section === sec.id);
        return (
          <div key={sec.id} data-testid={`academy-section-${sec.id}`}>
            <div className="mt-6 mb-3 text-[11px] font-mono font-bold uppercase tracking-[0.08em] text-ink-muted">
              {"// "}{sec.label}
            </div>
            {vids.length === 0 ? (
              <div className="rounded-xl border border-dashed border-line bg-bg-elevated/50 px-6 py-8 text-center text-xs text-ink-muted"
                   data-testid={`academy-empty-${sec.id}`}>
                No tutorials here yet — this section fills in as videos are produced.
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {vids.map((v) => (
                  <VideoCard key={v.video_id} v={v} isAdmin={data.is_admin}
                             onPlay={() => setPlaying(v)}
                             onDelete={async () => {
                               if (!await appConfirm(`Delete "${v.title}"?`)) return;
                               await api.delete(`/academy/videos/${v.video_id}`);
                               toast.success("Video removed"); load();
                             }} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {playing && <PlayerModal video={playing} onClose={(refresh) => { setPlaying(null); if (refresh) load(); }} />}
      {showAdd && <AddVideoModal onClose={(refresh) => { setShowAdd(false); if (refresh) load(); }} />}
    </div>
  );
}

function statusLine(v) {
  if (v.watched) return { text: "✓ Watched", cls: "text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded text-[10px] font-bold" };
  if (v.last_position_seconds > 0 && v.duration_seconds > 0)
    return { text: `${fmt(Math.max(0, v.duration_seconds - v.last_position_seconds))} left`, cls: "text-ink-muted text-[11px]" };
  return { text: "Not started", cls: "text-ink-muted text-[11px]" };
}

function VideoCard({ v, isAdmin, onPlay, onDelete }) {
  const st = statusLine(v);
  const pct = v.watched ? 100 : (v.duration_seconds ? Math.min(100, Math.round((v.last_position_seconds / v.duration_seconds) * 100)) : 0);
  return (
    <div className="group overflow-hidden rounded-xl border border-line bg-bg-surface cursor-pointer hover:border-sky-500/40 transition"
         onClick={onPlay} data-testid={`academy-video-card-${v.video_id}`}>
      <div className="relative aspect-video bg-gradient-to-br from-[#1a2535] to-[#0d1e35] flex items-center justify-center">
        {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="relative z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 backdrop-blur group-hover:bg-sky-500/30 transition">
          <Play className="h-4 w-4 text-white" fill="white" />
        </div>
        {v.duration_seconds > 0 && (
          <div className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white">
            {fmt(v.duration_seconds)}
          </div>
        )}
        {pct > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/15">
            <div className="h-full bg-sky-400" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="flex items-start justify-between gap-2 px-3.5 py-3">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold truncate">{v.title}</div>
          <div className="mt-1"><span className={st.cls} data-testid={`academy-status-${v.video_id}`}>{st.text}</span></div>
        </div>
        {isAdmin && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} data-testid={`academy-delete-${v.video_id}`}
            className="text-ink-muted hover:text-rose-400 transition shrink-0 mt-0.5">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function PlayerModal({ video, onClose }) {
  const emb = embedFor(video.video_url);
  const ref = useRef(null);
  const lastSent = useRef(0);
  const changed = useRef(false);

  const sendProgress = (pos, watched = null) => {
    changed.current = true;
    api.post(`/academy/videos/${video.video_id}/progress`,
      watched === null ? { position_seconds: Math.floor(pos) } : { position_seconds: Math.floor(pos), watched })
      .catch(() => {});
  };

  const onTime = () => {
    const el = ref.current;
    if (!el) return;
    if (el.currentTime - lastSent.current >= 10 || (el.duration && el.currentTime >= el.duration * 0.9 && lastSent.current < el.duration * 0.9)) {
      lastSent.current = el.currentTime;
      sendProgress(el.currentTime);
    }
  };

  return (
    <Dialog open onOpenChange={() => {
      const el = ref.current;
      if (el && el.currentTime > 0) sendProgress(el.currentTime);
      onClose(changed.current);
    }}>
      <DialogContent className="max-w-3xl bg-bg-surface border-line" data-testid="academy-player-modal">
        <DialogHeader><DialogTitle className="font-display">{video.title}</DialogTitle></DialogHeader>
        {emb.kind === "iframe" ? (
          <>
            <iframe src={emb.src} title={video.title} className="aspect-video w-full rounded-lg border border-line"
                    allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            {!video.watched && (
              <button onClick={() => { sendProgress(video.duration_seconds || 0, true); toast.success("Marked as watched"); }}
                data-testid="academy-mark-watched-btn"
                className="self-end rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20 transition">
                ✓ Mark as watched
              </button>
            )}
          </>
        ) : (
          <video ref={ref} src={emb.src} controls autoPlay className="aspect-video w-full rounded-lg border border-line bg-black"
                 onTimeUpdate={onTime}
                 onLoadedMetadata={() => { if (ref.current && video.last_position_seconds > 5 && !video.watched) ref.current.currentTime = video.last_position_seconds; }}
                 onEnded={() => sendProgress(video.duration_seconds || ref.current?.duration || 0, true)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddVideoModal({ onClose }) {
  const [f, setF] = useState({ title: "", section: "getting_started", video_url: "", duration: "", thumbnail_url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.title.trim() || !f.video_url.trim()) { toast.error("Title and video URL are required"); return; }
    setBusy(true);
    try {
      const { data: v } = await api.post("/academy/videos", {
        title: f.title, section: f.section, video_url: f.video_url,
        thumbnail_url: f.thumbnail_url, duration_seconds: parseDur(f.duration),
      });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/academy/videos/${v.video_id}/thumbnail`, fd);
      }
      toast.success("Video added");
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add video");
    } finally { setBusy(false); }
  };

  const inp = "w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-sky-500/50";
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md bg-bg-surface border-line" data-testid="academy-add-modal">
        <DialogHeader><DialogTitle className="font-display">Add tutorial video</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input className={inp} placeholder="Title" value={f.title} data-testid="add-video-title"
                 onChange={(e) => setF({ ...f, title: e.target.value })} />
          <select className={inp} value={f.section} data-testid="add-video-section"
                  onChange={(e) => setF({ ...f, section: e.target.value })}>
            {SECTION_META.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <input className={inp} placeholder="Video URL (YouTube, Vimeo, or direct MP4)" value={f.video_url}
                 data-testid="add-video-url" onChange={(e) => setF({ ...f, video_url: e.target.value })} />
          <input className={inp} placeholder="Duration (e.g. 3:12)" value={f.duration}
                 data-testid="add-video-duration" onChange={(e) => setF({ ...f, duration: e.target.value })} />
          <input className={inp} placeholder="Thumbnail URL (optional)" value={f.thumbnail_url}
                 data-testid="add-video-thumb-url" onChange={(e) => setF({ ...f, thumbnail_url: e.target.value })} />
          <div>
            <label className="text-[11px] text-ink-muted">…or upload a thumbnail image</label>
            <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="add-video-thumb-file"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="mt-1 block w-full text-xs text-ink-secondary file:mr-3 file:rounded file:border-0 file:bg-bg-elevated file:px-3 file:py-1.5 file:text-xs file:text-ink-primary" />
          </div>
          <button onClick={save} disabled={busy} data-testid="add-video-save"
                  className="w-full rounded-md bg-brand-amber py-2.5 text-sm font-bold text-black hover:opacity-90 transition disabled:opacity-60">
            {busy ? "Saving…" : "Add video"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
